import React, { useEffect, useState, useCallback } from 'react';
import { getTrackById, Track } from './Utils';
import { User } from 'firebase/auth';
import './NextTrack.css';

let tracks: Track[];
if (process.env.REACT_APP_BUILD_ENV === 'production') {
  tracks = require('./tracks.prod.json');
} else {
  tracks = require('./tracks.staging.json');
}

interface NextTrackProps {
    userId: string;
    user: User | null;
}

const NextTrack: React.FC<NextTrackProps> = ({ userId, user }) => {
    const [trackId, setTrackId] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [stats, setStats] = useState<any | null>(null);
    const [statsError, setStatsError] = useState<string | null>(null);
    const [statsLoading, setStatsLoading] = useState(false);

    const fetchNextTrack = useCallback(() => {
        if (!userId || !user) return;
        setLoading(true);
        setError(null);
        const apiUrl = process.env.REACT_APP_API_URL;
        user.getIdToken().then(token => {
            fetch(`${apiUrl}/user/next-track?user_id=${encodeURIComponent(userId)}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            })
                .then(res => {
                    if (!res.ok) throw new Error('Failed to fetch next track');
                    return res.json();
                })
                .then(data => {
                    setTrackId(data.next_track_id);
                })
                .catch(e => setError(e.message))
                .finally(() => setLoading(false));
        });
    }, [userId, user]);

    const fetchUserStats = useCallback(() => {
        if (!userId || !user) return;
        setStatsLoading(true);
        setStatsError(null);
        const apiUrl = process.env.REACT_APP_API_URL;
        user.getIdToken().then(token => {
            fetch(`${apiUrl}/user/statistics?user_id=${encodeURIComponent(userId)}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            })
                .then(res => {
                    if (!res.ok) throw new Error('Failed to fetch user statistics');
                    return res.json();
                })
                .then(data => {
                    setStats(data);
                })
                .catch(e => setStatsError(e.message))
                .finally(() => setStatsLoading(false));
        });
    }, [userId, user]);

    useEffect(() => {
        fetchNextTrack();
        fetchUserStats();
    }, [fetchNextTrack, fetchUserStats]);

    function formatPlaytime(millis: string | number) {
        const ms = typeof millis === 'string' ? parseInt(millis, 10) : millis;
        const secs = Math.floor(ms / 1000);
        const mins = Math.floor(secs / 60);
        const hours = Math.floor(mins / 60);
        const remMins = mins % 60;
        return `${hours}h ${remMins}m`;
    }

    if (!userId || !user) return null;

    return (
        <div className="next-track-container">
            <button
                onClick={() => { fetchNextTrack(); fetchUserStats(); }}
                disabled={loading || statsLoading}
                className="next-track-refresh-btn"
            >
                {(loading || statsLoading) ? 'Refreshing...' : 'Refresh Next Track & Stats'}
            </button>
            {loading && <div className="next-track-loading">Loading next track...</div>}
            {error && <div className="next-track-error">Error: {error}</div>}
            {!loading && !error && !trackId && (
                <div className="next-track-empty">No next track found.</div>
            )}
            {!loading && !error && trackId && (() => {
                const track = getTrackById(trackId, tracks as Track[]);
                if (!track)
                    return <div className="next-track-notfound">Track not found: {trackId}</div>;
                return (
                    <div className="next-track-info">
                        <strong>Next Recommended Track:</strong>
                        <span>Title: {track.title || track.name}</span>
                        <span>Name: {track.name}</span>
                    </div>
                );
            })()}
            {/* User statistics section */}
            <div className="next-track-info">
                {statsLoading && <div>Loading statistics...</div>}
                {statsError && <div className="next-track-error">Error: {statsError}</div>}
                {!statsLoading && !statsError && stats && (
                    <div className="user-stats-info">
                        <strong>User Statistics:</strong>
                        <div>Total Tracks Played: {stats.total_tracks_played}</div>
                        <div>Total Playtime: {formatPlaytime(stats.total_playtime)}</div>
                        <div>Current Streak: {stats.current_streak}</div>
                        <div>Longest Streak: {stats.longest_streak}</div>
                        <div>Last Played Track: {(() => {
                            const lastTrack = getTrackById(stats.last_played_track_id, tracks as Track[]);
                            if (!lastTrack) return stats.last_played_track_id;
                            return `${lastTrack.title || lastTrack.name} (${lastTrack.name})`;
                        })()}</div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default NextTrack;
